/* Built In Imports */
import NextLink from 'next/link';
import { useState } from 'react';

/* External Imports */
import {
  Box,
  Flex,
  Image,
  Link,
  ListItem,
  UnorderedList
} from '@chakra-ui/react';
import { nanoid } from 'nanoid';
import { FaAngleDown, FaAngleUp, FaTimes } from 'react-icons/fa';
import { useMediaQuery } from 'react-responsive';

/* Internal Imports */
/* Components */
import { refineUrlDomain } from '@components/Utility/utils';
import config from '@config';

/* Services */
/* Styles */

/**
 * @param {string} region - Current User Region
 * @param {string} language - Current User Language
 * @param {string} headerData - Menus from API
 */

const Navbar = ({ headerData, region, lang }) => {
  return (
    <Box
      margin="0"
      padding="0"
      width="100%"
      position="relative"
      // zIndex="99"
      background="#FFF5DD"
      transition="all .6s ease !important"
    >
      <Box height="80px" display="flex" alignItems="center" m="auto">
        <Flex
          w="100%"
          maxW="1170px"
          alignItems="center"
          justifyContent="space-between"
          px={{ base: '10px', xl: '0' }}
          m="auto"
        >
          <NextLink
            href={refineUrlDomain('/project-samskriti', region, lang)}
            passHref
            legacyBehavior>
            <Link
              minWidth="115px"
            >
              <Image
                alt="logo"
                title="logo"
                src={`${config.staticPath}/d/46272/1663650889-project-logo.svg`}
              />
            </Link>
          </NextLink>
          <Box>
            <Menu headerData={headerData} region={region} lang={lang} />
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

const Menu = ({ headerData, region, lang }) => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  return isMobile ? (
    <MobileNavbar menus={headerData} region={region} lang={lang} />
  ) : (
    <DesktopNavbar menus={headerData} region={region} lang={lang} />
  );
};

const DesktopNavbar = ({ menus, region, lang }) => {
  return (
    <Flex w="750px">
      <UnorderedList
        listStyleType={'none'}
        display="flex"
        gridGap="50px"
      >
        {menus?.map(menu => {
          return (
            <DesktopMenu
              key={nanoid(4)}
              item={menu}
              region={region}
              lang={lang}
            />
          );
        })}
      </UnorderedList>
    </Flex>
  );
};

const DesktopMenu = ({ item, region, lang }) => {
  const [open, setOpen] = useState(false);

  return (
    <ListItem
      position="relative"
      paddingTop={!!item?.url > 0 ? '25px' : '25px'}
      paddingBottom={!!item?.url > 0 ? '18px' : '18px'}
      borderBottom="7px solid transparent"
      className="Menu"
      _hover={{
        borderBottom:
          !!item?.url > 0 ? '7px transparent #cf4520' : '7px solid #cf4520',
        bgColor: !!item?.url > 0 ? '#FFF5DD' : 'none',
      }}
      cursor="pointer"
      onMouseEnter={e => setOpen(true)}
      onMouseLeave={e => setOpen(false)}
    >
      <Flex alignItems="center">
        {!!item?.url > 0 ? (
          <NextLink href={refineUrlDomain(item?.url, region, lang)} passHref legacyBehavior>
            <Link
              fontFamily="'FedraSansStd-book', sans-serif"
              fontSize="16px"
              color="#404040"
              fontWeight="400"
              borderRight="0"
              _hover={{ textDecor: 'none' }}
              lineHeight="28px"
              textDecoration="none"
              padding="1px"
            >
              {item?.title}
            </Link>
          </NextLink>
        ) : (
          <Link
            fontFamily="'FedraSansStd-book', sans-serif"
            fontSize="16px"
            color="#404040"
            borderRight="0"
            _hover={{ textDecor: 'none' }}
            lineHeight="1.25"
            textDecoration="none"
            padding="5px 15px"
          >
            {item?.title}
          </Link>
        )}
        {item?.submenuItems?.length ? (
          open ? (
            <FaAngleUp />
          ) : (
            <FaAngleDown />
          )
        ) : null}
      </Flex>

      {item?.submenuItems?.length ? (
        <Box
          pos={'absolute'}
          width="250px"
          height="auto"
          top="80px"
          right="0"
          transition="all 0.3s ease-out"
          display={open ? 'block' : 'none'}
          background="#FFFFFF"
          className="SubItem"
          zIndex={open && '100'}
          borderRadius="0px 0px 2px 2px"
        >
          <UnorderedList listStyleType={'none'} gridGap="5px" m="7px">
            {item?.submenuItems?.map(menu => {
              return (
                <DesktopSubmenu
                  key={nanoid(4)}
                  menu={menu}
                  region={region}
                  lang={lang}
                />
              );
            })}
          </UnorderedList>
        </Box>
      ) : null}
    </ListItem>
  );
};

const DesktopSubmenu = ({ menu, region, lang }) => {
  return (
    <ListItem
      w={'100%'}
      position="relative"
      // paddingTop={'5px'}
      // paddingBottom={'5px'}
      // pr={'7px'}
      // p="7px"
      mb="5px"
      // borderBottom="7px solid transparent"
      className="Menu"
      cursor="pointer"
    >
      <Flex alignItems="center">
        <NextLink href={refineUrlDomain(menu.url, region, lang)} passHref legacyBehavior>
          <Link
            fontFamily="'FedraSansStd-book', sans-serif"
            fontSize="14px"
            color="#404040"
            width="100%"
            fontWeight="400"
            p="7px"
            _hover={{
              bgColor: '#FFF5DD',
              textDecor: 'none'
            }}
            lineHeight="28px"
            textDecoration="none"
          // padding="1px"
          >
            {menu?.title}
          </Link>
        </NextLink>
      </Flex>
    </ListItem>
  );
};

const MobileNavbar = ({ menus, region, lang }) => {
  const [open, setOpen] = useState(false);

  const handleClick = e => {
    e.preventDefault();
    setOpen(prev => !prev);
  };

  return (
    <Box>
      <Box
        pos={'absolute'}
        display={open ? 'block' : 'none'}
        zIndex="5"
        top={'100px'}
        left="0"
        backgroundColor={'rgba(0, 0, 0, 0.45)'}
        h="100vh"
        width={'100%'}
      />
      <Box
        margin={'auto 0'}
        display={{ lg: 'none', md: 'block' }}
        w="20px"
        pos={'absolute'}
        height={'20px'}
        onClick={handleClick}
        cursor="pointer"
        top="30px"
        right={'21px'}
      >
        <style>
          {`
          .cross{
            font-size:40px !important;
            font-weight:300;
          }
          `}
        </style>
        {open ? (
          <FaTimes
            className="cross"
            style={{ marginRight: '20px', width: '100%', marginTop: '-10px' }}
          />
        ) : (
          <FaBars style={{ marginRight: '20px' }} />
        )}
      </Box>
      {open ? (
        <Flex
          d={{ lg: 'none', md: 'flex', sm: 'flex', base: 'flex' }}
          flexDir={'column'}
          marginLeft="5px"
          top="80px"
          position="absolute"
          left="-5px"
          width="100%"
          background="#ffffff"
          zIndex="99"
          transition="all .6s ease"
          padding="15px 20px"
        >
          <UnorderedList
            listStyleType={'none'}
            margin="0"
            display="flex"
            flexDir={'column'}
          >
            {menus?.map(menu => {
              return (
                <MobileMenu
                  key={nanoid(4)}
                  item={menu}
                  region={region}
                  lang={lang}
                />
              );
            })}
          </UnorderedList>
        </Flex>
      ) : null}
    </Box>
  );
};

const MobileMenu = ({ item, region, lang }) => {
  const [expanded, setExpanded] = useState(false);

  const handleClick = e => {
    e.preventDefault();
    setExpanded(prev => !prev);
  };

  return (
    <ListItem
      position="relative"
      width={'100%'}
      paddingTop={item?.url ? '25px' : '25px'}
      paddingBottom={item?.url ? '25px' : '25px'}
      borderBottom={item?.url ? '1px solid #CFCFCF' : '1px solid #CFCFCF'}
      onClick={handleClick}
      cursor="pointer"
      className="smMenu"
    >
      <Flex justifyContent={'space-between'} >
        {item?.url ? (
          <NextLink href={refineUrlDomain(item?.url, region, lang)} passHref legacyBehavior>
            <Link
              fontFamily="FedraSansStd-medium"
              fontSize="16px"
              color="#404040"
              fontWeight="400"
              borderRight="0"
              _hover={{ textDecor: 'none' }}
              lineHeight="28px"
              textDecoration="none"
              padding="1px 15px"
            >
              {item?.title}
            </Link>
          </NextLink>
        ) : (
          <Link
            fontFamily="FedraSansStd-medium"
            fontSize="16px"
            color={expanded ? '#A81B14' : '#404040'}
            fontWeight={expanded ? '500' : '400'}
            borderRight="0"
            _hover={{ textDecor: 'none' }}
            lineHeight="1.25"
            textDecoration="none"
            padding="5px 15px"
          >
            {item?.title}
          </Link>
        )}

        {item?.submenuItems?.length ? (
          expanded ? (
            <FaAngleUp />
          ) : (
            <FaAngleDown />
          )
        ) : null}
      </Flex>
      {item?.submenuItems?.length ? (
        <Box
          width={'100%'}
          display={expanded ? 'block' : 'none'}
          top="80px"
          background="#FFFFFF"
          className="SubItem"
          borderRadius="0px 0px 2px 2px"
          transition="all 0.3s ease-out"
          zIndex={expanded && '100'}
        >
          <UnorderedList listStyleType={'none'} gridGap="5px">
            {item?.submenuItems?.map(menu => {
              return (
                <MobileSubMenu
                  key={nanoid(4)}
                  menu={menu}
                  region={region}
                  lang={lang}
                />
              );
            })}
          </UnorderedList>
        </Box>
      ) : null}
    </ListItem>
  );
};

const MobileSubMenu = ({ menu, region, lang }) => {
  return (
    <ListItem
      position="relative"
      width={'100%'}
      paddingTop="10px"
      paddingBottom="5px"
      borderBottom="none"
      cursor="pointer"
    >
      <Flex justifyContent={'space-between'}>
        <NextLink href={refineUrlDomain(menu?.url, region, lang)} passHref legacyBehavior>
          <Link
            fontFamily="'FedraSansStd-book', sans-serif"
            fontSize="14px"
            color="#404040"
            fontWeight="400"
            borderRight="0"
            _hover={{ textDecor: 'none' }}
            lineHeight="28px"
            textDecoration="none"
            padding="1px 15px"
            width="100%"
          >
            {menu?.title}
          </Link>
        </NextLink>
      </Flex>
    </ListItem>
  );
};

export default Navbar;

const FaBars = () => {
  return (
    <Box>
      <Flex flexDir={'column'} justifyContent="space-between">
        <Box
          width="23px"
          height="3px"
          background="#210303"
          borderRadius="2px"
          mb="4px"
        ></Box>
        <Box
          width="23px"
          height="3px"
          background="#210303"
          borderRadius="2px"
          mb="4px"
        ></Box>
        <Box
          width="23px"
          height="3px"
          background="#210303"
          borderRadius="2px"
          mb="4px"
        ></Box>
      </Flex>
    </Box>
  );
};
